/**
 * Internal-traffic tagging for GA4.
 *
 * The hard problem: "which users in GA4 are real, vs me / demo / QA / App-Review
 * accounts?" An IP filter (GA4 Admin → Internal Traffic) catches us on our home
 * network, but misses us on a phone, another network, or incognito. This tags
 * the GA4 session by the LOGGED-IN ACCOUNT instead — so a known-internal email
 * is excluded no matter the device or network.
 *
 * It sets `traffic_type = 'internal'`, which GA4's built-in **Internal Traffic
 * data filter matches** — so you exclude both the IP rule and these accounts
 * with the SAME one filter. We also set an `internal_user` user property for
 * explicit segmentation in Explorations.
 *
 * To maintain: edit INTERNAL_EMAILS as your test roster changes.
 */

/**
 * Whole domains that are always internal (your company + the test domain).
 * Every address @these is excluded.
 */
const INTERNAL_DOMAINS: ReadonlySet<string> = new Set([
  'jwcholding.com', // your company — any address here is you
  'socialworktestprepacademy.com', // our own domain — pam@, support@, appreview@, etc.
  'example.com', // test domain (student.demo@, testuser@, …)
  // NOT pcmilestones.com — some of Pam's employees are REAL studying users; only her own mailbox below.
]);

/**
 * Known internal mailboxes, stored NORMALIZED (lowercased, +tag stripped, and
 * dots stripped for Gmail). Because we normalize before comparing, EVERY alias
 * of these is caught automatically — e.g. jordancoinjackson@gmail.com also
 * matches jordan.coin.jackson@gmail.com and jordancoinjackson+anything@gmail.com,
 * and pennyquarter@aol.com matches pennyquarter+test@aol.com.
 */
const INTERNAL_MAILBOX_LIST: readonly string[] = [
  'jordancoinjackson@gmail.com', // you (+ all dot/plus aliases)
  'jwilliamcj29@gmail.com', // you
  'theboldlyflying@gmail.com', // you
  'bestfilmstills@gmail.com', // you
  'lenard56insider@gmail.com', // you (Jordan, 2026-09-06)
  'pennyquarter@aol.com', // mom — test payer (+ plus aliases)
  'pennycoin@gmail.com', // mom — test free (+ aliases)
  'pam@pcmilestones.com', // Pam (co-founder) — her own address only, NOT the whole domain (her employees are real users)
  // appreview@ now covered by the socialworktestprepacademy.com domain above.
];

/**
 * `+tag` values that flag a deliberate test alias on ANY address — so you can
 * spin up `yourname+test@gmail.com`, `whatever+qa@…` etc. and they're excluded
 * without editing this file.
 */
const TEST_TAGS: readonly string[] = ['test', 'qa', 'demo', 'internal', 'staging', 'dev'];

/** localStorage flag so a returning internal device re-tags before login resolves. */
const FLAG_KEY = 'swtpa_internal_traffic';

/** Normalize an email the way mail providers actually route it. */
function normalizeEmail(raw: string): { mailbox: string; domain: string; tag: string } {
  const e = raw.trim().toLowerCase();
  const at = e.lastIndexOf('@');
  if (at < 0) return { mailbox: e, domain: '', tag: '' };
  const domain = e.slice(at + 1);
  let local = e.slice(0, at);

  // +tag is ignored by the mailbox (most providers); capture it, then drop it.
  const plus = local.indexOf('+');
  const tag = plus >= 0 ? local.slice(plus + 1) : '';
  if (plus >= 0) local = local.slice(0, plus);

  // Gmail ignores dots in the local part.
  if (domain === 'gmail.com' || domain === 'googlemail.com') local = local.replace(/\./g, '');

  return { mailbox: `${local}@${domain}`, domain, tag };
}

/** Known mailboxes, normalized the same way as inputs so any alias matches. */
const INTERNAL_MAILBOXES: ReadonlySet<string> = new Set(
  INTERNAL_MAILBOX_LIST.map((e) => normalizeEmail(e).mailbox)
);

/**
 * True for any account that is you, your mom, a test domain, or a deliberate
 * test alias — robust to Gmail dot/plus aliases, not a brittle exact list.
 */
export function isInternalEmail(email?: string | null): boolean {
  if (!email) return false;
  const { mailbox, domain, tag } = normalizeEmail(email);
  if (INTERNAL_DOMAINS.has(domain)) return true;
  if (INTERNAL_MAILBOXES.has(mailbox)) return true;
  if (tag && TEST_TAGS.some((t) => tag === t || tag.includes(t))) return true;
  return false;
}

// gtag's typed signature is narrow; use a permissive alias for the `set` calls.
type GtagFn = (...args: unknown[]) => void;

/** Tag the live GA4 session as internal. No-op if GA isn't loaded (e.g. dev). */
export function markInternalTraffic(): void {
  if (typeof window === 'undefined') return;
  const g = (window as unknown as { gtag?: GtagFn }).gtag;
  if (typeof g !== 'function') return;
  g('set', { traffic_type: 'internal' }); // matches GA4's Internal Traffic filter
  g('set', 'user_properties', { internal_user: 'true' });
}

/**
 * Call on every auth state change. Tags internal users (and remembers the device
 * so we can re-tag on the next visit); clears the flag when a real user signs in.
 */
export function applyInternalTrafficForUser(email?: string | null): void {
  if (typeof window === 'undefined') return;
  if (isInternalEmail(email)) {
    try { window.localStorage.setItem(FLAG_KEY, '1'); } catch { /* private mode */ }
    markInternalTraffic();
  } else if (email) {
    // A genuine, non-internal user signed in on this browser — stop tagging.
    try { window.localStorage.removeItem(FLAG_KEY); } catch { /* ignore */ }
  }
}

/** Call once on app boot: re-tag a known-internal device as early as possible. */
export function initInternalTrafficFromFlag(): void {
  if (typeof window === 'undefined') return;
  try {
    if (window.localStorage.getItem(FLAG_KEY) === '1') markInternalTraffic();
  } catch { /* ignore */ }
}

/**
 * True when THIS browser is flagged internal via the localStorage flag (set by
 * `?internal=1`). This is the anonymous-page counterpart to the email check:
 * on /try there's no logged-in address to key off, so the founder opts their
 * own device out with a URL toggle. Safe in every environment (SSR/private mode).
 */
export function isInternalTraffic(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Honor an `?internal=1` / `?internal=0` toggle on anonymous pages (e.g. /try),
 * where no logged-in email is available. `?internal=1` (or any non-"0" value)
 * persists the flag so the founder's own testing is excluded from GA4 on every
 * event; `?internal=0` clears it. When set, also tags the live GA4 session
 * immediately. Absent the param, the existing flag is left untouched.
 */
export function applyInternalTrafficFromQuery(search?: string): void {
  if (typeof window === 'undefined') return;
  let value: string | null;
  try {
    value = new URLSearchParams(search ?? window.location.search).get('internal');
  } catch {
    return;
  }
  if (value === null) return; // param absent — don't touch the flag
  if (value === '0' || value === 'false') {
    try { window.localStorage.removeItem(FLAG_KEY); } catch { /* ignore */ }
    return;
  }
  // Any other value ("1", "true", …) opts this device in.
  try { window.localStorage.setItem(FLAG_KEY, '1'); } catch { /* private mode */ }
  markInternalTraffic();
}
